export interface ProductColor {
  name: string
  hex: string
}

export interface ProductAddOn {
  id: string
  name: string
  priceDelta: number
}

export interface Product {
  id: string
  name: string
  slug: string
  category: string
  price: number
  salePrice?: number
  images: string[]
  description: string
  sizes?: string[]
  colors?: ProductColor[]
  addOns?: ProductAddOn[]
  stockBySize?: Record<string, number>
  rating: number
  reviewsCount: number
  isFeatured?: boolean
}

// Shared fallback image (Maldives beach) used if a product image fails to load.
export const FALLBACK_PRODUCT_IMAGE =
  'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?auto=format&fit=crop&q=80&w=1200'

export const products: Product[] = [
  {
    id: 'p1',
    name: 'Maldives Beach Towel',
    slug: 'beach-towel',
    category: 'accessories',
    price: 29.99,
    images: [
      'https://images.unsplash.com/photo-1535571393765-ea44927160be?auto=format&fit=crop&q=80&w=1200',
      'https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&q=80&w=1200',
    ],
    description:
      'Ultra-soft, highly absorbent cotton beach towel inspired by the colours of the Maldivian lagoon. Perfect for lounging on white sand or drying off after a swim.',
    colors: [
      { name: 'Lagoon Blue', hex: '#0ea5e9' },
      { name: 'Sunset Coral', hex: '#f97316' },
      { name: 'White Sand', hex: '#f5f5f4' },
    ],
    addOns: [
      { id: 'embroidery', name: 'Monogram embroidery', priceDelta: 50 },
      { id: 'giftwrap', name: 'Gift wrap', priceDelta: 25 },
    ],
    rating: 4.8,
    reviewsCount: 124,
    isFeatured: true,
  },
  {
    id: 'p2',
    name: 'Classic Sunglasses',
    slug: 'classic-sunglasses',
    category: 'accessories',
    price: 49.99,
    salePrice: 39.99,
    images: [
      'https://images.unsplash.com/photo-1572635196237-14b3f281503f?auto=format&fit=crop&q=80&w=1200',
      'https://images.unsplash.com/photo-1577803645773-f96470509666?auto=format&fit=crop&q=80&w=1200',
    ],
    description:
      'Timeless design with UV400 protection. Lightweight frame for all-day comfort under the Maldivian sun.',
    colors: [
      { name: 'Tortoise', hex: '#78350f' },
      { name: 'Matte Black', hex: '#1c1917' },
    ],
    addOns: [
      { id: 'polarized', name: 'Polarized upgrade', priceDelta: 80 },
      { id: 'hardcase', name: 'Hard case', priceDelta: 40 },
    ],
    rating: 4.9,
    reviewsCount: 86,
    isFeatured: true,
  },
  {
    id: 'p3',
    name: "Men's Swim Shorts",
    slug: 'mens-swim-shorts',
    category: 'swimwear',
    price: 34.99,
    images: [
      'https://images.unsplash.com/photo-1591195853828-11db59a44f6b?auto=format&fit=crop&q=80&w=1200',
      'https://images.unsplash.com/photo-1565693413579-8a73cd02763a?auto=format&fit=crop&q=80&w=1200',
    ],
    description:
      'Quick-drying swim shorts with an elastic waistband and mesh lining. Features side pockets and a back zip pocket — built for island hopping.',
    sizes: ['S', 'M', 'L', 'XL'],
    stockBySize: { S: 5, M: 12, L: 0, XL: 3 },
    colors: [
      { name: 'Ocean Navy', hex: '#1e3a8a' },
      { name: 'Reef Coral', hex: '#f43f5e' },
    ],
    rating: 4.5,
    reviewsCount: 42,
    isFeatured: true,
  },
  {
    id: 'p4',
    name: 'Comfort Beach Sandals',
    slug: 'comfort-beach-sandals',
    category: 'footwear',
    price: 24.99,
    images: [
      'https://images.unsplash.com/photo-1603487742131-4160ec999306?auto=format&fit=crop&q=80&w=1200',
      'https://images.unsplash.com/photo-1531310197839-ccf54634509e?auto=format&fit=crop&q=80&w=1200',
    ],
    description:
      'Ergonomic footbed sandals designed for long walks on Maldivian beaches. Water-resistant, durable, and lightweight.',
    sizes: ['38', '39', '40', '41', '42', '43', '44'],
    stockBySize: {
      '38': 2,
      '39': 5,
      '40': 8,
      '41': 10,
      '42': 6,
      '43': 0,
      '44': 1,
    },
    colors: [
      { name: 'Sand Taupe', hex: '#a8a29e' },
      { name: 'Reef Black', hex: '#000000' },
    ],
    rating: 4.7,
    reviewsCount: 215,
    isFeatured: true,
  },
  {
    id: 'p5',
    name: 'Woven Sun Hat',
    slug: 'woven-sun-hat',
    category: 'accessories',
    price: 35.0,
    images: [
      'https://images.unsplash.com/photo-1622760807800-66cf1466fc08?auto=format&fit=crop&q=80&w=1200',
      'https://images.unsplash.com/photo-1565839412847-3c97b4ed53d6?auto=format&fit=crop&q=80&w=1200',
    ],
    description:
      'Wide-brimmed straw hat to keep you cool and protected from the sun. Hand-woven with a stylish ribbon band — perfect for resort days.',
    sizes: ['One Size'],
    stockBySize: { 'One Size': 15 },
    rating: 4.6,
    reviewsCount: 58,
    isFeatured: true,
  },
  {
    id: 'p6',
    name: 'Snorkeling Set',
    slug: 'snorkeling-set',
    category: 'accessories',
    price: 89.99,
    salePrice: 69.99,
    images: [
      'https://images.unsplash.com/photo-1583212292454-1fe6229603b7?auto=format&fit=crop&q=80&w=1200',
      'https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&q=80&w=1200',
    ],
    description:
      'Premium silicone mask and dry-top snorkel. Tempered glass lens for maximum clarity exploring Maldivian reefs.',
    colors: [
      { name: 'Reef Yellow', hex: '#eab308' },
      { name: 'Lagoon Aqua', hex: '#06b6d4' },
    ],
    addOns: [
      { id: 'antifog', name: 'Anti-fog spray', priceDelta: 15 },
      { id: 'fins', name: 'Add matching fins', priceDelta: 120 },
    ],
    rating: 4.9,
    reviewsCount: 312,
    isFeatured: true,
  },
  {
    id: 'p7',
    name: 'UV Rash Guard',
    slug: 'uv-rash-guard',
    category: 'swimwear',
    price: 45.0,
    images: [
      'https://images.unsplash.com/photo-1556228852-80b6e5eeff06?auto=format&fit=crop&q=80&w=1200',
      'https://images.unsplash.com/photo-1556228720-da4e85bc9e8b?auto=format&fit=crop&q=80&w=1200',
    ],
    description:
      'Long-sleeve UPF 50+ rash guard for maximum sun protection while swimming, snorkeling, or paddling. Quick-drying, lightweight, and breathable.',
    sizes: ['XS', 'S', 'M', 'L', 'XL'],
    stockBySize: { XS: 3, S: 8, M: 15, L: 5, XL: 2 },
    colors: [
      { name: 'Reef Black', hex: '#000000' },
      { name: 'Lagoon Blue', hex: '#0ea5e9' },
      { name: 'Palm Emerald', hex: '#10b981' },
    ],
    rating: 4.8,
    reviewsCount: 94,
    isFeatured: true,
  },
  {
    id: 'p8',
    name: 'Maldives Dhoni Souvenir',
    slug: 'maldives-dhoni-souvenir',
    category: 'souvenirs',
    price: 12.99,
    images: [
      'https://images.unsplash.com/photo-1586500036706-41963de24d8b?auto=format&fit=crop&q=80&w=1200',
      'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?auto=format&fit=crop&q=80&w=1200',
    ],
    description:
      'Hand-carved wooden dhoni — the traditional Maldivian fishing boat. A perfect keepsake from your island holiday.',
    rating: 4.4,
    reviewsCount: 38,
  },
  {
    id: 'p9',
    name: 'Beach Cover-Up Kaftan',
    slug: 'beach-cover-up-kaftan',
    category: 'swimwear',
    price: 55.0,
    salePrice: 39.0,
    images: [
      'https://images.unsplash.com/photo-1583846783214-7229a91b20ed?auto=format&fit=crop&q=80&w=1200',
      'https://images.unsplash.com/photo-1583846112692-21de2a17e10b?auto=format&fit=crop&q=80&w=1200',
    ],
    description:
      'Lightweight, breathable kaftan-style beach cover-up. Easy to slip on over swimwear — perfect for transitioning from beach to lunch.',
    sizes: ['S', 'M', 'L', 'XL'],
    stockBySize: { S: 4, M: 10, L: 8, XL: 3 },
    colors: [
      { name: 'White Sand', hex: '#f5f5f4' },
      { name: 'Palm Green', hex: '#10b981' },
    ],
    rating: 4.7,
    reviewsCount: 67,
  },
  {
    id: 'p10',
    name: 'Reef-Safe Sunscreen',
    slug: 'reef-safe-sunscreen',
    category: 'accessories',
    price: 18.5,
    images: [
      'https://images.unsplash.com/photo-1556228578-8c89e6adf883?auto=format&fit=crop&q=80&w=1200',
      'https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?auto=format&fit=crop&q=80&w=1200',
    ],
    description:
      'Reef-safe, mineral-based SPF 50+ sunscreen. Protects your skin and Maldivian coral reefs at the same time.',
    rating: 4.9,
    reviewsCount: 178,
    isFeatured: true,
  },
  {
    id: 'p11',
    name: 'Maldives Postcard Set',
    slug: 'maldives-postcard-set',
    category: 'souvenirs',
    price: 8.99,
    images: [
      'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?auto=format&fit=crop&q=80&w=1200',
      'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?auto=format&fit=crop&q=80&w=1200',
    ],
    description:
      'A set of 10 premium postcards featuring iconic Maldivian scenes — water bungalows, lagoons, and dhonis.',
    rating: 4.5,
    reviewsCount: 52,
  },
  {
    id: 'p12',
    name: 'Beach Tote Bag',
    slug: 'beach-tote-bag',
    category: 'accessories',
    price: 32.0,
    images: [
      'https://images.unsplash.com/photo-1591561954557-26941169b49e?auto=format&fit=crop&q=80&w=1200',
      'https://images.unsplash.com/photo-1566150905458-1bf1fc113f0d?auto=format&fit=crop&q=80&w=1200',
    ],
    description:
      'Spacious woven tote bag to carry all your beach essentials. Sturdy handles and water-resistant lining.',
    colors: [
      { name: 'Natural Straw', hex: '#d6b370' },
      { name: 'Sunset Coral', hex: '#fb7185' },
    ],
    addOns: [{ id: 'monogram', name: 'Monogram', priceDelta: 40 }],
    rating: 4.6,
    reviewsCount: 71,
  },
]
